import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";

import {
  MagnifyingGlassIcon,
  PlusIcon,
  ShareIcon,
  PencilIcon,
  CopyIcon,
  TrashIcon,
  CheckIcon,
  XIcon,
  DotsThreeVerticalIcon,
  ArrowSquareOutIcon,
  UsersIcon,
} from "@phosphor-icons/react";
import { Popover, Tooltip } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  useWorkspaces,
  groupByTime,
} from "../FpFundBrain/hooks/useWorkspacesSimple";
import { v4 as uuidv4 } from "uuid";
import fpFetch from "../../services/fpFetch";
import { BASE_API_URL, BASE_API_URL_V2 } from "../../constants";
import { toConversationSlug } from "./Chat/chatUtils";
import {
  chatBackendKindForId,
  shareEntityTypeForChatId,
} from "./Chat/selectChatBackend";
import { AgenticChatBackend } from "./Chat/AgenticChatBackend";
import { ShareModal } from "@/components/common/ShareModal";
import { useSidebar, useSidebarRevealHold } from "./SidebarContext";
import { resetFpAppV4Data, updateFpAppV4Data } from "../../redux/fpAppV4Slice";
import { handleError } from "../../utils/handleError";

// The Recents section (#12209, after Claude's sidebar): an Owner control
// switches whose list it is (the ownership groups: My chats, Shared with me,
// each team, the organization), remembered per product. The heading does not
// collapse the list (Hunter: "what's the point of hiding the history just to
// show blankness there?").
const recentsOwnerKey = (kind: string) => `sidebar-recents-owner:${kind}`;

/** The remembered owner group's name, or "" for the first (the user's own). */
function readRecentsOwner(kind: string): string {
  try {
    return localStorage.getItem(recentsOwnerKey(kind)) ?? "";
  } catch {
    return "";
  }
}
function writeRecentsOwner(kind: string, owner: string): void {
  try {
    if (owner) localStorage.setItem(recentsOwnerKey(kind), owner);
    else localStorage.removeItem(recentsOwnerKey(kind));
  } catch {
    // Storage unavailable: the choice lasts for the session only.
  }
}

// The Owner menu's paper and rows take the design system's action Menu look
// (RMS/design/Menu.tsx: 7px radius, the one shadow, 30px 13px rows) so it
// reads as the same product; it is a picker with a checked item, which that
// Menu does not model, so the rows are its own.
const OWNER_PAPER_SX = {
  width: 200,
  borderRadius: "7px",
  border: "1px solid var(--fp-border)",
  backgroundColor: "var(--fp-surface)",
  boxShadow: "var(--fp-lift)",
  backgroundImage: "none",
  padding: "4px",
  marginTop: "4px",
};
const OWNER_ROW_CLASS =
  "flex h-[30px] w-full items-center gap-2 rounded-[5px] px-2 text-left text-[13px] text-[var(--fp-ink)] hover:bg-[var(--fp-hover)] focus-visible:bg-[var(--fp-hover)] focus-visible:outline-none focus-visible:shadow-[inset_0_0_0_1px_var(--fp-border-strong)] active:bg-[var(--fp-ink-wash)]";

/** The Owner menu: one row per ownership group, the current one checked;
 *  a click switches the list and closes the menu. */
const OwnerMenu: React.FC<{
  groups: ReadonlyArray<{ name: string; count: number }>;
  value: string;
  onChange: (name: string) => void;
}> = ({ groups, value, onChange }) => (
  <div>
    {groups.map((group) => {
      const checked = group.name === value;
      return (
        <button
          key={group.name}
          type="button"
          role="menuitemradio"
          aria-checked={checked}
          onClick={() => onChange(group.name)}
          className={OWNER_ROW_CLASS}
        >
          <span className="flex-1 truncate">{group.name}</span>
          {group.count === 0 && (
            <span className="text-[12px] text-[var(--fp-ink-3)]">empty</span>
          )}
          {checked && (
            <CheckIcon
              className="h-3.5 w-3.5 flex-none text-[var(--fp-ink-2)]"
              aria-hidden
            />
          )}
        </button>
      );
    })}
  </div>
);

interface WorkspaceHistoryProps {
  workspaceType: string;
  supportsWorkspaces: boolean;
  allowedProducts: string[];
  userIsOrgAdmin: boolean;
}

const WorkspaceHistory: React.FC<WorkspaceHistoryProps> = ({
  workspaceType,
  supportsWorkspaces,
  allowedProducts,
  userIsOrgAdmin,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { workspaceHistoryKey } = useSidebar();

  // Use the workspace type from the key to ensure consistency
  const effectiveWorkspaceType = useMemo(() => {
    // The key should match the workspace type, but fallback to the prop if needed
    if (
      workspaceHistoryKey === "chat" ||
      workspaceHistoryKey === "matrix" ||
      workspaceHistoryKey === "reports" ||
      workspaceHistoryKey === "report_instance"
    ) {
      return workspaceHistoryKey;
    }
    return workspaceType;
  }, [workspaceHistoryKey, workspaceType]);

  // Context menu state
  const [contextMenuWorkspaceId, setContextMenuWorkspaceId] = useState<
    string | null
  >(null);
  const [contextMenuPosition, setContextMenuPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [hoveredWorkspaceId, setHoveredWorkspaceId] = useState<string | null>(
    null
  );
  const [renamingWorkspaceId, setRenamingWorkspaceId] = useState<string | null>(
    null
  );
  const [renameValue, setRenameValue] = useState("");
  const contextMenuRef = useRef<HTMLDivElement>(null);

  // Share modal state
  const [shareWorkspaceId, setShareWorkspaceId] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);

  // Search modal state
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [searchModalTerm, setSearchModalTerm] = useState("");

  // Local workspace modifications for optimistic updates
  const [localWorkspaceModifications, setLocalWorkspaceModifications] =
    useState<{
      renamed: Record<string, string>; // workspaceId -> new title
      deleted: Set<string>; // workspaceIds that are deleted
      added: Array<{ id: string; title: string; type: string; date: Date }>; // newly added workspaces
    }>({
      renamed: {},
      deleted: new Set(),
      added: [],
    });

  // Skeleton loader component for workspaces
  const WorkspaceSkeleton = () => (
    <div className="space-y-4">
      {/* Today section */}
      <div>
        <div className="text-xs font-semibold text-[#e6e4df]/55 mb-2">
          <div className="h-3 w-16 bg-white/10 rounded animate-pulse"></div>
        </div>
        <div className="space-y-1">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="px-2 py-3 rounded-lg bg-white/5">
              <div className="h-4 w-3/4 bg-white/10 rounded animate-pulse mb-1"></div>
              <div className="h-3 w-1/2 bg-white/10 rounded animate-pulse"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Tab state
  const [activeTabImpl, setActiveTab] = useState<"team" | "files">("team");
  const activeTab = supportsWorkspaces ? activeTabImpl : "files";

  // Team tab functionality
  const [searchTerm, setSearchTerm] = useState("");

  // The Recents section's own state (#12209): whose list it is, remembered.
  // The owner is keyed by product, so a chat choice never narrows the
  // matrices list.
  const [selectedGroupName, setSelectedGroupNameState] = useState<string>(() =>
    readRecentsOwner(effectiveWorkspaceType)
  );
  const setSelectedGroupName = useCallback(
    (name: string) => {
      setSelectedGroupNameState(name);
      writeRecentsOwner(effectiveWorkspaceType, name);
    },
    [effectiveWorkspaceType]
  );
  const [ownerAnchor, setOwnerAnchor] = useState<HTMLElement | null>(null);
  // Their papers are portalled outside the panel: hold the hover reveal
  // while any is open, or opening one collapses the panel under it.
  useSidebarRevealHold(
    ownerAnchor !== null || showShareModal || isSearchModalOpen
  );
  // Where "View all" goes: the product's collection page. Chat has none on
  // desktop (the chats hub is phone-only; Gerry: "lets not have that for
  // now"), so chat shows no View all.
  const viewAllHref: string | null =
    effectiveWorkspaceType === "chat"
      ? null
      : effectiveWorkspaceType === "matrix"
        ? "/matrix"
        : effectiveWorkspaceType === "report_instance"
          ? "/reports"
          : "/report-templates";
  const newLabel =
    effectiveWorkspaceType === "matrix"
      ? "New matrix"
      : effectiveWorkspaceType === "report_instance"
        ? "New report"
        : effectiveWorkspaceType === "report"
          ? "New template"
          : "New chat";

  // Always call both hooks to maintain hook order consistency
  const workspaceData = useWorkspaces(
    effectiveWorkspaceType as "chat" | "matrix" | "report_instance" | "report",
    searchTerm
  );

  // Get unfiltered groups for search modal (always use empty searchTerm to get all workspaces)
  const unfilteredWorkspaceData = useWorkspaces(
    effectiveWorkspaceType as "chat" | "matrix" | "report_instance" | "report",
    "" // Empty string to get all workspaces for search modal
  );

  const { groups, loading, error, refresh } = workspaceData;

  // Unfiltered groups for search modal
  const unfilteredGroups = unfilteredWorkspaceData.groups;

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        contextMenuRef.current &&
        !contextMenuRef.current.contains(event.target as Node)
      ) {
        setContextMenuWorkspaceId(null);
        setContextMenuPosition(null);
      }
    };

    if (contextMenuWorkspaceId) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [contextMenuWorkspaceId]);

  // Listen for workspace updates and refresh
  useEffect(() => {
    const handleWorkspaceUpdate = () => refresh();
    window.addEventListener("workspaceUpdated", handleWorkspaceUpdate);
    return () =>
      window.removeEventListener("workspaceUpdated", handleWorkspaceUpdate);
  }, [refresh]);

  // Listen for specific workspace title updates for efficient local updates
  useEffect(() => {
    const handleWorkspaceTitleUpdate = (event: CustomEvent) => {
      const { workspaceId, newTitle } = event.detail;

      // Update the local modifications to reflect the new title
      setLocalWorkspaceModifications((prev) => ({
        ...prev,
        renamed: {
          ...prev.renamed,
          [workspaceId]: newTitle,
        },
      }));
    };

    window.addEventListener(
      "workspaceTitleUpdated",
      handleWorkspaceTitleUpdate as EventListener
    );
    return () =>
      window.removeEventListener(
        "workspaceTitleUpdated",
        handleWorkspaceTitleUpdate as EventListener
      );
  }, []);

  // Enhanced refresh function that clears local modifications
  const refreshWithClear = useCallback(() => {
    setLocalWorkspaceModifications({
      renamed: {},
      deleted: new Set(),
      added: [],
    });
    refresh();
  }, [refresh]);

  // Apply local modifications to groups
  const modifiedGroups = useMemo(() => {
    return groups.map((group) => ({
      ...group,
      workspaces: [
        // Add any new workspaces for this group
        ...localWorkspaceModifications.added
          .filter((added) => {
            // Add to "My [type]" group for simplicity
            return group.name.startsWith("My ");
          })
          .map((added) => ({
            id: added.id,
            title: added.title,
            subtitle: "",
            date: added.date,
          })),
        // Filter out deleted workspaces and apply renames
        ...group.workspaces
          .filter(
            (workspace) =>
              !localWorkspaceModifications.deleted.has(workspace.id)
          )
          .map((workspace) => ({
            ...workspace,
            title:
              localWorkspaceModifications.renamed[workspace.id] ||
              workspace.title,
          })),
      ],
    }));
  }, [groups, localWorkspaceModifications]);

  // Apply local modifications to unfiltered groups for search modal
  const unfilteredModifiedGroups = useMemo(() => {
    return unfilteredGroups.map((group) => ({
      ...group,
      workspaces: [
        // Add any new workspaces for this group
        ...localWorkspaceModifications.added
          .filter((added) => {
            // Add to "My [type]" group for simplicity
            return group.name.startsWith("My ");
          })
          .map((added) => ({
            id: added.id,
            title: added.title,
            subtitle: "",
            date: added.date,
          })),
        // Filter out deleted workspaces and apply renames
        ...group.workspaces
          .filter(
            (workspace) =>
              !localWorkspaceModifications.deleted.has(workspace.id)
          )
          .map((workspace) => ({
            ...workspace,
            title:
              localWorkspaceModifications.renamed[workspace.id] ||
              workspace.title,
          })),
      ],
    }));
  }, [unfilteredGroups, localWorkspaceModifications]);

  // Helper function to match and score workspaces for search
  // Returns: null if no match, 2 for exact match, 1 for word match
  const matchWorkspace = useCallback(
    (workspaceTitle: string, searchTerm: string): number | null => {
      if (!searchTerm.trim()) return null;

      const searchLower = searchTerm.toLowerCase().trim();
      const titleLower = workspaceTitle.toLowerCase();

      // Exact match (case-insensitive)
      if (titleLower === searchLower) {
        return 2;
      }

      // Contains match (exact substring)
      if (titleLower.includes(searchLower)) {
        return 2;
      }

      // Word-based matching: if search has spaces, check if all words are present
      const searchWords = searchLower.split(/\s+/).filter((w) => w.length > 0);
      if (searchWords.length > 1) {
        const titleWords = titleLower.split(/\s+/).filter((w) => w.length > 0);
        const allWordsMatch = searchWords.every((searchWord) =>
          titleWords.some((titleWord) => titleWord.includes(searchWord))
        );
        if (allWordsMatch) {
          return 1;
        }
      }

      return null;
    },
    []
  );

  // Handle keyboard events for search modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isSearchModalOpen) return;

      if (event.key === "Escape") {
        setIsSearchModalOpen(false);
        setSearchModalTerm("");
      } else if (event.key === "Enter" && searchModalTerm.trim()) {
        // Use unfiltered groups for search modal to ensure all workspaces are available
        const allWorkspacesMap = new Map<
          string,
          (typeof unfilteredModifiedGroups)[0]["workspaces"][0]
        >();
        unfilteredModifiedGroups.forEach((group) => {
          group.workspaces.forEach((workspace) => {
            if (!allWorkspacesMap.has(workspace.id)) {
              allWorkspacesMap.set(workspace.id, workspace);
            }
          });
        });
        const allWorkspaces = Array.from(allWorkspacesMap.values());

        // Filter and score workspaces
        const scoredResults = allWorkspaces
          .map((workspace) => ({
            workspace,
            score: matchWorkspace(workspace.title, searchModalTerm),
          }))
          .filter((result) => result.score !== null)
          .sort((a, b) => (b.score || 0) - (a.score || 0)); // Higher score first

        if (scoredResults.length > 0) {
          // Close FileViewer if it's open by dispatching the close event
          window.dispatchEvent(new CustomEvent("closeDocumentPanel"));
          // Navigate to the workspace
          if (effectiveWorkspaceType === "chat") {
            navigate(
              `/chat/${toConversationSlug(scoredResults[0].workspace.id)}`,
              {
                state: {
                  chatBackendKind: chatBackendKindForId(
                    scoredResults[0].workspace.id
                  ),
                },
              }
            );
          } else {
            navigate(
              `/${effectiveWorkspaceType === "report_instance" ? "reports" : effectiveWorkspaceType}/${scoredResults[0].workspace.id}`
            );
          }
          setIsSearchModalOpen(false);
          setSearchModalTerm("");
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    isSearchModalOpen,
    searchModalTerm,
    unfilteredModifiedGroups,
    matchWorkspace,
    navigate,
    effectiveWorkspaceType,
  ]);

  // The owner group the user chose, or the first (their own) when the choice
  // is unset or names a group that no longer exists.
  const selectedGroup = useMemo(
    () =>
      (selectedGroupName &&
        modifiedGroups.find((g) => g.name === selectedGroupName)) ||
      modifiedGroups[0] ||
      null,
    [modifiedGroups, selectedGroupName]
  );
  const filteredWorkspaces = useMemo(
    () => selectedGroup?.workspaces || [],
    [selectedGroup]
  );
  // A group other than the user's own is named in the heading, so the list
  // says whose it is; the user's own reads "Recents".
  const ownerIsOther = selectedGroup !== null && selectedGroup.kind !== "own";
  // The heading has one line: the group's short label (the organization
  // group reads "Organization" rather than "<Organization name> Organization
  // chats"; Gerry: "look bad when org is long name"), which still truncates
  // when a team's name is long.
  const headingLabel = ownerIsOther ? selectedGroup.label : "Recents";
  // A heading that truncates gets a tooltip with the group's full name
  // (Gerry: "if the name overflows, tooltip"); one that fits gets none.
  const headingRef = useRef<HTMLSpanElement>(null);
  const [headingOverflows, setHeadingOverflows] = useState(false);
  useEffect(() => {
    const el = headingRef.current;
    if (!el) return;
    const measure = () =>
      setHeadingOverflows(el.scrollWidth > el.clientWidth + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [headingLabel]);
  const headingTitle =
    headingOverflows && ownerIsOther ? selectedGroup.name : "";

  // Group the filtered workspaces by time
  const timeGroups = useMemo(() => {
    return groupByTime(filteredWorkspaces);
  }, [filteredWorkspaces]);

  const createNewWorkspace = async () => {
    // Close FileViewer if it's open by dispatching the close event
    window.dispatchEvent(new CustomEvent("closeDocumentPanel"));

    // For chat, just navigate to chat starter instead of creating a workspace
    if (effectiveWorkspaceType === "chat") {
      navigate("/chat");
      return;
    }

    // For reports, create a new report workspace
    if (effectiveWorkspaceType === "report_instance") {
      try {
        const id = uuidv4();
        const type = "private_doc_editor"; // Report instance type

        // Redux dispatch is synchronous, so the reset is already applied by
        // the time the next dispatch runs — no wait needed between them.
        dispatch(resetFpAppV4Data());
        dispatch(
          updateFpAppV4Data({
            workspaceId: id,
            title: "New Report",
            type,
            trigger: "clean_editor",
          })
        );

        await fpFetch(`${BASE_API_URL}/workspaces/update`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ _id: id, title: "New Report", type }),
        });

        navigate(`/reports/${id}`);
      } catch (e) {
        handleError({ error: e, location: "createNewReport" });
      }
      return;
    }

    // For reports, navigate to reports page instead of creating a workspace
    if (effectiveWorkspaceType === "report") {
      navigate("/reports");
      return;
    }

    // For matrix, create a new matrix workspace
    try {
      const newWorkspaceId = uuidv4();

      // Create the workspace with the correct matrix type
      await fpFetch(`${BASE_API_URL}/workspaces/update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          _id: newWorkspaceId,
          type: "querygrid",
          title: "New Matrix",
          content: "",
          meta: {},
        }),
      });

      // Navigate to the new matrix with setup panel open
      const params = new URLSearchParams();
      params.append("sidepanel", "setup");
      navigate(`/matrix/${newWorkspaceId}?${params.toString()}`);
    } catch (error) {
      console.error("Error creating matrix:", error);
      window.openFpToast &&
        window.openFpToast("Failed to create matrix", "error");
    }
  };

  // Context menu handlers
  const handleContextMenu = (e: React.MouseEvent, workspaceId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenuWorkspaceId(workspaceId);
    setContextMenuPosition({ x: e.clientX, y: e.clientY });
  };

  const handleShareWorkspace = async (workspaceId: string) => {
    setShareWorkspaceId(workspaceId);
    setShowShareModal(true);
    setContextMenuWorkspaceId(null);
  };

  const handleRenameWorkspace = (workspaceId: string, currentTitle: string) => {
    setRenamingWorkspaceId(workspaceId);
    setRenameValue(
      currentTitle ||
        `New ${workspaceType === "matrix" ? "Matrix" : workspaceType === "report_instance" ? "Report" : workspaceType === "report" ? "Template" : "Chat"}`
    );
    setContextMenuWorkspaceId(null);
  };

  const handleSaveRename = async (workspaceId: string) => {
    if (!renameValue.trim()) {
      setRenamingWorkspaceId(null);
      return;
    }

    // Optimistic update
    setLocalWorkspaceModifications((prev) => ({
      ...prev,
      renamed: { ...prev.renamed, [workspaceId]: renameValue.trim() },
    }));
    setRenamingWorkspaceId(null);
    setRenameValue("");

    try {
      if (effectiveWorkspaceType === "chat") {
        await fpFetch(
          `${BASE_API_URL_V2}/api/v2/chat/conversations/${workspaceId}`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ title: renameValue.trim() }),
          }
        );
      } else {
        await fpFetch(`${BASE_API_URL}/workspaces/update`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            _id: workspaceId,
            title: renameValue.trim(),
            type: workspaceType === "matrix" ? "querygrid" : "chat",
          }),
        });
      }
    } catch (error) {
      console.error("Error renaming workspace:", error);
      // Revert optimistic update on error
      setLocalWorkspaceModifications((prev) => {
        const { [workspaceId]: removed, ...rest } = prev.renamed;
        return { ...prev, renamed: rest };
      });
    }
  };

  const handleCancelRename = () => {
    setRenamingWorkspaceId(null);
    setRenameValue("");
  };

  const handleWorkspaceNavigation = (workspaceId: string) => {
    // Close FileViewer if it's open by dispatching the close event
    window.dispatchEvent(new CustomEvent("closeDocumentPanel"));
    // Navigate to the workspace
    if (effectiveWorkspaceType === "chat") {
      // Chat URLs are the bare conversation UUID; the id shape tells the chat
      // page which backend created it so it loads from the right store (#8147).
      navigate(`/chat/${toConversationSlug(workspaceId)}`, {
        state: { chatBackendKind: chatBackendKindForId(workspaceId) },
      });
      return;
    }
    navigate(
      `/${effectiveWorkspaceType === "report_instance" ? "reports" : effectiveWorkspaceType}/${workspaceId}`
    );
  };

  const handleCopyWorkspace = async (workspaceId: string) => {
    try {
      window.openFpToast && window.openFpToast("Copying...", "info");

      // An agentic conversation is not a workspace document — it lives in
      // chat_conversations and copies through its own backend. Routing it to
      // /workspaces/clone was a guaranteed 500 (#10499).
      if (chatBackendKindForId(workspaceId) === "agentic") {
        const copyId = await new AgenticChatBackend().cloneWorkspace(
          workspaceId
        );
        refresh();
        window.openFpToast &&
          window.openFpToast("Copy successfully made", "success");
        navigate(`/chat/${toConversationSlug(copyId)}`, {
          state: { chatBackendKind: "agentic" },
        });
        setContextMenuWorkspaceId(null);
        return;
      }

      // First get the original workspace to get its title
      const originalResponse = await fpFetch(
        `${BASE_API_URL}/workspaces/find?_id=${workspaceId}`
      );
      const originalWorkspace = await originalResponse.json();

      // Use the proper clone endpoint
      const response = await fpFetch(`${BASE_API_URL}/workspaces/clone`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ _id: workspaceId, instantiateTemplate: false }),
      });

      if (!response.ok) {
        throw new Error("Error copying workspace");
      }

      const data = await response.json();
      const newWorkspaceId = data.workspaceId;
      const workspaceTypeFromResponse = data.type;

      // Update the title to include "(Copy)"
      const originalTitle =
        originalWorkspace.title ||
        `New ${workspaceType === "matrix" ? "Matrix" : workspaceType === "report_instance" ? "Report" : workspaceType === "report" ? "Template" : "Chat"}`;
      const copyTitle = `${originalTitle} (Copy)`;

      await fpFetch(`${BASE_API_URL}/workspaces/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          _id: newWorkspaceId,
          title: copyTitle,
          type: workspaceTypeFromResponse,
        }),
      });

      // Refresh the workspace list to show the new workspace
      refresh();

      window.openFpToast &&
        window.openFpToast("Copy successfully made", "success");

      // Navigate to the new workspace
      navigate(
        `/${workspaceType === "report_instance" ? "reports" : workspaceType}/${newWorkspaceId}`
      );
    } catch (error) {
      console.error("Error copying workspace:", error);
      window.openFpToast &&
        window.openFpToast("Failed to copy workspace", "error");
    }
    setContextMenuWorkspaceId(null);
  };

  const handleDeleteWorkspace = async (
    workspaceId: string,
    workspaceTitle: string
  ) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${workspaceTitle || `this ${workspaceType === "report_instance" ? "report" : workspaceType}`}"? This action cannot be undone.`
    );

    if (!confirmDelete) {
      setContextMenuWorkspaceId(null);
      return;
    }

    // Optimistic update - remove workspace from local state
    setLocalWorkspaceModifications((prev) => ({
      ...prev,
      deleted: new Set([...prev.deleted, workspaceId]),
    }));

    // If we're currently viewing the deleted workspace, navigate away
    if (location.pathname.includes(workspaceId)) {
      navigate(
        `/${workspaceType === "report_instance" ? "reports" : workspaceType}`
      );
    }

    try {
      if (effectiveWorkspaceType === "chat") {
        await fpFetch(
          `${BASE_API_URL_V2}/api/v2/chat/conversations/${workspaceId}`,
          { method: "DELETE" }
        );
      } else {
        await fpFetch(`${BASE_API_URL}/workspaces/delete`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ _id: workspaceId }),
        });
      }
    } catch (error) {
      console.error("Error deleting workspace:", error);
      // Revert optimistic update on error
      setLocalWorkspaceModifications((prev) => {
        const newDeleted = new Set(prev.deleted);
        newDeleted.delete(workspaceId);
        return { ...prev, deleted: newDeleted };
      });
      // Navigate back to the workspace if we had navigated away
      if (
        location.pathname ===
        `/${workspaceType === "report_instance" ? "reports" : workspaceType}`
      ) {
        navigate(
          `/${workspaceType === "report_instance" ? "reports" : workspaceType}/${workspaceId}`
        );
      }
    }
    setContextMenuWorkspaceId(null);
  };

  return (
    <div>
      {/* The Recents heading row (#12209, after Claude's sidebar): View all
          shows while the row is hovered or focused (always on phone, which
          has no hover); owner, search and new are always there. */}
      <div
        data-recents-head=""
        className="group flex items-center gap-0.5 bg-transparent pb-2"
      >
        <Tooltip
          title={headingTitle}
          placement="bottom-start"
          enterDelay={300}
          describeChild
        >
          <h2 className="flex min-w-0 items-center px-1 py-1 text-[13px] font-medium text-[#e6e4df]/55">
            <span ref={headingRef} className="truncate">
              {headingLabel}
            </span>
          </h2>
        </Tooltip>

        <div className="ml-auto flex items-center text-[#e6e4df]/55">
          {viewAllHref && (
            <Link
              to={viewAllHref}
              aria-label="View all"
              className="rounded-md p-1.5 opacity-0 transition-[opacity,color] hover:text-[#e6e4df] focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 group-hover:opacity-100 group-focus-within:opacity-100 max-md:opacity-100"
            >
              <ArrowSquareOutIcon className="h-4 w-4" />
            </Link>
          )}
          <button
            type="button"
            aria-label="Owner"
            aria-haspopup="menu"
            aria-expanded={ownerAnchor !== null}
            data-active={ownerIsOther ? "true" : "false"}
            onClick={(event) => setOwnerAnchor(event.currentTarget)}
            // Whose list this is (Gerry: "a way to change chat owner
            // easily, so instead of a filter, do a person icon"): lit
            // while open or showing someone else's.
            className={`rounded-md p-1.5 transition-colors hover:text-[#e6e4df] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 ${
              ownerIsOther || ownerAnchor !== null ? "text-[#e6e4df]" : ""
            }`}
          >
            <UsersIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Search"
            className="rounded-md p-1.5 transition-colors hover:text-[#e6e4df] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
            onClick={() => setIsSearchModalOpen(true)}
          >
            <MagnifyingGlassIcon className="h-4 w-4" />
          </button>
          <a
            href={
              effectiveWorkspaceType === "chat"
                ? "/chat"
                : effectiveWorkspaceType === "report_instance" ||
                    effectiveWorkspaceType === "report"
                  ? "/reports/new"
                  : "/matrix/new"
            }
            aria-label={newLabel}
            className="rounded-md p-1.5 transition-colors hover:text-[#e6e4df] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
            onClick={(e) => {
              if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) {
                return;
              }
              e.preventDefault();
              createNewWorkspace();
            }}
          >
            <PlusIcon className="h-4 w-4" />
          </a>
        </div>
      </div>

      {/* The Owner menu: the ownership groups, the current one checked. A
          picker, so not the design system's action Menu, but wearing its
          paper and rows. It hangs from the control's left edge and grows
          rightward over the page beside the panel. */}
      <Popover
        open={ownerAnchor !== null}
        anchorEl={ownerAnchor}
        onClose={() => setOwnerAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        marginThreshold={8}
        slotProps={{
          paper: {
            // A menu of checked items: the rows are menuitemradio, so the
            // paper is their menu.
            role: "menu",
            "aria-label": "Owner",
            sx: OWNER_PAPER_SX,
          },
        }}
      >
        <OwnerMenu
          groups={modifiedGroups.map((g) => ({
            name: g.name,
            count: g.workspaces.length,
          }))}
          value={selectedGroup?.name ?? ""}
          onChange={(name) => {
            const picked = modifiedGroups.find((g) => g.name === name);
            setSelectedGroupName(picked?.kind === "own" ? "" : name);
            setOwnerAnchor(null);
          }}
        />
      </Popover>

      {/* Workspace list */}
      <div>
        {loading ? (
          <WorkspaceSkeleton />
        ) : filteredWorkspaces.length === 0 ? (
          <div className="flex justify-center items-center h-20">
            <div className="text-sm text-[#e6e4df]/55 text-center">
              No{" "}
              {effectiveWorkspaceType === "matrix"
                ? "matrices"
                : effectiveWorkspaceType === "report_instance"
                  ? "reports"
                  : effectiveWorkspaceType === "report"
                    ? "templates"
                    : "chats"}{" "}
              yet
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {timeGroups.map((timeGroup) => (
              <div key={timeGroup.title}>
                <div className="text-xs font-semibold text-[#e6e4df]/55 mb-2">
                  {timeGroup.title}
                </div>
                {/* Rows at the product rows' rhythm (36px, 2px apart;
                    Gerry: "too much space between chats"). */}
                <div className="space-y-0.5">
                  {timeGroup.workspaces.map((workspace) => (
                    <div
                      key={workspace.id}
                      className="relative group"
                      onMouseEnter={() => setHoveredWorkspaceId(workspace.id)}
                      onMouseLeave={() => setHoveredWorkspaceId(null)}
                    >
                      {renamingWorkspaceId === workspace.id ? (
                        <div className="px-2 py-2 rounded-lg bg-white/10 border border-[var(--fp-accent)]">
                          <input
                            type="text"
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                handleSaveRename(workspace.id);
                              } else if (e.key === "Escape") {
                                handleCancelRename();
                              }
                            }}
                            onBlur={() => handleSaveRename(workspace.id)}
                            className="w-full text-sm max-md:text-[16px] font-medium text-[#e6e4df] bg-transparent border-none outline-none"
                            autoFocus
                          />
                          <div className="flex gap-1 mt-1">
                            <button
                              onClick={() => handleSaveRename(workspace.id)}
                              className="p-1 text-emerald-400 hover:bg-white/10 rounded"
                            >
                              <CheckIcon className="w-3 h-3" />
                            </button>
                            <button
                              onClick={handleCancelRename}
                              className="p-1 text-rose-400 hover:bg-white/10 rounded"
                            >
                              <XIcon className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <Link
                            to={`/${effectiveWorkspaceType === "report_instance" ? "reports" : effectiveWorkspaceType}/${toConversationSlug(workspace.id)}`}
                            onClick={(e) => {
                              if (
                                e.ctrlKey ||
                                e.metaKey ||
                                e.shiftKey ||
                                e.button === 1
                              ) {
                                return;
                              }
                              handleWorkspaceNavigation(workspace.id);
                            }}
                            className={`w-full text-left block px-2 py-2 rounded-lg transition-colors ${
                              location.pathname ===
                              `/${effectiveWorkspaceType === "report_instance" ? "reports" : effectiveWorkspaceType}/${toConversationSlug(workspace.id)}`
                                ? "bg-white/10"
                                : "hover:bg-white/10"
                            }`}
                          >
                            {/* Phone drawer legibility (#11742 round 5
                                T2, CD): history titles are the drawer's
                                most-read strings — they step 14→16 with
                                the nav labels. */}
                            <div className="text-sm max-md:text-[16px] font-regular truncate">
                              {workspace.title ||
                                `New ${effectiveWorkspaceType === "matrix" ? "Matrix" : effectiveWorkspaceType === "report_instance" ? "Report" : effectiveWorkspaceType === "report" ? "Template" : "Chat"}`}
                            </div>
                          </Link>

                          {/* Three-dot menu button */}
                          {hoveredWorkspaceId === workspace.id && (
                            <button
                              onClick={(e) =>
                                handleContextMenu(e, workspace.id)
                              }
                              className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 rounded hover:bg-white/10 transition-colors"
                            >
                              <DotsThreeVerticalIcon className="w-4 h-4 text-[#e6e4df]/55" />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Context Menu (via portal to escape parent stacking contexts) */}
      {contextMenuWorkspaceId &&
        contextMenuPosition &&
        createPortal(
          <div
            ref={contextMenuRef}
            className="fixed z-50 bg-[var(--fp-surface)] border border-[var(--fp-border-strong)] rounded-lg shadow-lg py-1 min-w-[160px]"
            style={{
              left: contextMenuPosition.x,
              top: contextMenuPosition.y,
              zIndex: 2147483647,
            }}
          >
            <button
              onClick={() => handleShareWorkspace(contextMenuWorkspaceId)}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-[var(--fp-ink)] hover:bg-[var(--fp-hover)] transition-colors"
            >
              <ShareIcon className="w-4 h-4" />
              Share
            </button>
            <button
              onClick={() => {
                const workspace = filteredWorkspaces.find(
                  (w) => w.id === contextMenuWorkspaceId
                );
                handleRenameWorkspace(
                  contextMenuWorkspaceId,
                  workspace?.title || ""
                );
              }}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-[var(--fp-ink)] hover:bg-[var(--fp-hover)] transition-colors"
            >
              <PencilIcon className="w-4 h-4" />
              Rename
            </button>

            <button
              onClick={() => handleCopyWorkspace(contextMenuWorkspaceId)}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-[var(--fp-ink)] hover:bg-[var(--fp-hover)] transition-colors"
            >
              <CopyIcon className="w-4 h-4" />
              Make Copy
            </button>
            <div className="border-t border-gray-200 my-1" />
            <button
              onClick={() => {
                const workspace = filteredWorkspaces.find(
                  (w) => w.id === contextMenuWorkspaceId
                );
                handleDeleteWorkspace(
                  contextMenuWorkspaceId,
                  workspace?.title || ""
                );
              }}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <TrashIcon className="w-4 h-4" />
              Delete
            </button>
          </div>,
          document.body
        )}

      {/* Share Modal */}
      {shareWorkspaceId && (
        <ShareModal
          open={showShareModal}
          onClose={() => {
            setShowShareModal(false);
            setShareWorkspaceId(null);
          }}
          // Agentic conversations (pgid-chat-conversations- ids) share via the
          // "conversation" endpoint; matrices, reports, and legacy chats keep
          // "workspace". Using "workspace" for a conversation makes reads hit
          // an empty table and writes 403 against the workspace auth check.
          entityType={shareEntityTypeForChatId(shareWorkspaceId)}
          entityId={shareWorkspaceId}
          entityName={
            filteredWorkspaces.find((w) => w.id === shareWorkspaceId)?.title ||
            `New ${effectiveWorkspaceType === "matrix" ? "Matrix" : effectiveWorkspaceType === "report_instance" ? "Report" : "Chat"}`
          }
        />
      )}

      {/* Search Modal */}
      {isSearchModalOpen &&
        createPortal(
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[999999]"
            onClick={() => {
              setIsSearchModalOpen(false);
              setSearchModalTerm("");
            }}
          >
            <div
              className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 h-[500px] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-lg font-semibold text-gray-900">
                  Search{" "}
                  {effectiveWorkspaceType === "matrix"
                    ? "Matrices"
                    : effectiveWorkspaceType === "report_instance"
                      ? "Reports"
                      : effectiveWorkspaceType === "report"
                        ? "Templates"
                        : "Chats"}
                </h2>
                <button
                  onClick={() => {
                    setIsSearchModalOpen(false);
                    setSearchModalTerm("");
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Search Input */}
              <div className="p-6 flex-shrink-0">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder={`Search ${effectiveWorkspaceType === "matrix" ? "matrices" : effectiveWorkspaceType === "report_instance" ? "reports" : effectiveWorkspaceType === "report" ? "templates" : "chats"}...`}
                    value={searchModalTerm}
                    onChange={(e) => setSearchModalTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    autoFocus
                  />
                </div>
              </div>

              {/* Search Results */}
              <div className="flex-1 overflow-y-auto border-t border-gray-200 min-h-0">
                {(() => {
                  // Get all workspaces from all groups, deduplicated by ID
                  // Use unfilteredModifiedGroups to ensure we get ALL workspaces (not filtered by searchTerm)
                  // This ensures we get all workspaces even if they appear in multiple groups
                  const allWorkspacesMap = new Map<
                    string,
                    (typeof unfilteredModifiedGroups)[0]["workspaces"][0]
                  >();
                  unfilteredModifiedGroups.forEach((group) => {
                    group.workspaces.forEach((workspace) => {
                      // Only add if we haven't seen this workspace ID before
                      // This prevents duplicate keys and ensures each workspace appears once
                      if (!allWorkspacesMap.has(workspace.id)) {
                        allWorkspacesMap.set(workspace.id, workspace);
                      }
                    });
                  });
                  const allWorkspaces = Array.from(allWorkspacesMap.values());

                  if (searchModalTerm === "") {
                    return (
                      <div className="p-6 text-center text-gray-500">
                        Start typing to search...
                      </div>
                    );
                  }

                  // Filter and score workspaces using word-based matching
                  const scoredResults = allWorkspaces
                    .map((workspace) => ({
                      workspace,
                      score: matchWorkspace(workspace.title, searchModalTerm),
                    }))
                    .filter((result) => result.score !== null)
                    .sort((a, b) => (b.score || 0) - (a.score || 0)); // Higher score first (exact matches first)

                  if (scoredResults.length === 0) {
                    return (
                      <div className="p-6 text-center text-gray-500">
                        No results found for "{searchModalTerm}"
                      </div>
                    );
                  }

                  return (
                    <div className="divide-y divide-gray-200">
                      {scoredResults.map((result) => (
                        <Link
                          key={result.workspace.id}
                          to={`/${effectiveWorkspaceType === "report_instance" ? "reports" : effectiveWorkspaceType}/${result.workspace.id}`}
                          className="block p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={(e) => {
                            if (
                              e.ctrlKey ||
                              e.metaKey ||
                              e.shiftKey ||
                              e.button === 1
                            ) {
                              return;
                            }
                            handleWorkspaceNavigation(result.workspace.id);
                            setIsSearchModalOpen(false);
                            setSearchModalTerm("");
                          }}
                        >
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {result.workspace.title}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {result.workspace.date.toLocaleDateString()} •{" "}
                            {result.workspace.date.toLocaleTimeString()}
                          </div>
                        </Link>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default WorkspaceHistory;
